import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import LeaseDealCard from "@/components/LeaseDealCard";
import { ChevronRight, Sparkles, ShieldCheck, Search } from "lucide-react";

export default function SeoLanding({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null);
    setErr(null);
    api.get(`/seo/landings/${slug}`)
      .then((r) => {
        setData(r.data);
        document.title = `${r.data.title} | 555 Auto Brokers`;
        const meta = document.querySelector('meta[name="description"]') || (() => {
          const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m;
        })();
        meta.content = r.data.subtitle;
      })
      .catch(() => setErr("Page not found"));
  }, [slug]);

  if (err) return <div className="max-w-[1200px] mx-auto px-5 py-24 text-gray-400">Page not found.</div>;
  if (!data) return <div className="max-w-[1200px] mx-auto px-5 py-24 text-gray-500">Loading…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14" data-testid={`seo-landing-${slug}`}>
      <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6">
        <Link to="/" className="hover:text-white">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/lease-deals" className="hover:text-white">Lease Deals</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-gray-300">{data.title}</span>
      </nav>

      <div className="max-w-3xl mb-12">
        <p className="label-mono mb-3 flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-emerald-400" /> Verified Only · 555 Auto Brokers</p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter mb-5" data-testid="seo-h1">{data.h1}</h1>
        <p className="text-lg text-gray-400 mb-3">{data.subtitle}</p>
        <p className="text-sm text-gray-500 leading-relaxed">{data.intro}</p>
      </div>

      {/* Deals grid */}
      {data.deals.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-20 text-center mb-16" data-testid="seo-empty">
          <p className="text-gray-400 mb-1">No verified offers match these filters today.</p>
          <p className="text-xs text-gray-500 mb-7">Our marketplace runs in Verified-Only mode. New matching offers will appear here as they're approved.</p>
          <div className="flex justify-center gap-3 flex-wrap">
            <Link to="/find-my-car" className="px-5 py-2.5 rounded-full silver-gradient text-[#050507] text-xs font-bold">Have us source one</Link>
            <Link to="/lease-deals" className="px-5 py-2.5 rounded-full border border-white/15 text-white text-xs font-bold hover:bg-white/5">All Lease Deals</Link>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-end justify-between mb-5">
            <p className="text-xs text-gray-500"><Sparkles className="w-3 h-3 inline mr-1 text-silver" /> {data.deals_count} verified offers · sorted by price</p>
            <Link to="/lease-deals" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="seo-all-deals">All filters <ChevronRight className="w-3 h-3" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {data.deals.map((d) => <LeaseDealCard key={d.id} deal={d} />)}
          </div>
        </>
      )}

      {/* FAQ */}
      {data.faq?.length > 0 && (
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-4">
            <p className="label-mono mb-3">Frequently Asked</p>
            <h2 className="font-display text-3xl font-light tracking-tight">Common questions on {data.title.toLowerCase()}.</h2>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {data.faq.map((q, i) => (
              <details key={i} className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-5 group" data-testid={`seo-faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between font-display text-lg">
                  {q[0]}
                  <ChevronRight className="w-4 h-4 text-silver group-open:rotate-90 transition" />
                </summary>
                <p className="text-sm text-gray-400 mt-3 leading-relaxed">{q[1]}</p>
              </details>
            ))}
          </div>
        </div>
      )}

      {/* Related */}
      {data.related?.length > 0 && (
        <div className="border-t border-white/5 pt-12">
          <p className="label-mono mb-4 flex items-center gap-2"><Search className="w-3 h-3" /> Keep exploring</p>
          <div className="flex flex-wrap gap-3">
            {data.related.map((s) => (
              <Link
                key={s}
                to={`/${s}`}
                className="px-4 py-2 rounded-full border border-white/10 text-sm text-gray-300 hover:bg-white/5 hover:border-white/20 hover:text-white transition"
                data-testid={`seo-related-${s}`}
              >
                {s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
